import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Icon, Input, Tooltip } from '@material-ui/core';
import {
  getServiceChargeEndAdornment,
  getServiceChargeStartAdornment,
  getServiceChargeValue,
  getServiceChargeBaseValue
} from './pricing-info-util';
import FormSubsection from '../../../../../widgets/FormSubsection/FormSubsection';
import DataTable from '../../../../../widgets/data-table/DataTable/DataTable';
import { injectIntl } from 'react-intl';
import * as RenewalStatus from '../../../../../../constants/RenewalStatus';
import {
  ADDITIONAL_CHARGES_PRECISION,
  PERCENTAGE_PRECISION
} from '../../../../../../constants/decimal-points';
import { checkPrecisionLength } from '../../../../../../utils/format-number-util';

const IMMUTABLE_SERVICE_CHARGE_DESCS = ['\\PREPCHARGE', '\\DELIVERYMIN'];

const serviceChargeDeviation = (serviceCharge) => {
  let currentValue = getServiceChargeValue(serviceCharge);
  let baseValue = getServiceChargeBaseValue(serviceCharge);

  currentValue = currentValue != null ? currentValue : 0;
  baseValue = baseValue != null ? baseValue : 0;

  return ((currentValue - baseValue) * 100) / (baseValue === 0 ? 1 : baseValue);
};

const getColumns = (
  props,
  setMutableServiceCharge,
  isMutable,
  revertServiceCharge,
  discardEditChanges,
  currentEditableCharge
) => [
  {
    header: {
      display: props.intl.formatMessage({
        id: 'description'
      })
    },
    accessor: (serviceCharge) => ({
      display: serviceCharge.specialChargeDesc
    }),
    shared: {
      className: 'only-padding-left-5'
    }
  },
  {
    header: {
      display: props.intl.formatMessage({
        id: 'amount'
      })
    },
    accessor: (serviceCharge) => {
      const adjustedCharge = getServiceChargeBaseValue(
        serviceCharge,
        props.roundValues
      );

      const baseCharge = getServiceChargeValue(
        serviceCharge,
        props.roundValues
      );

      const inEditMode =
        currentEditableCharge === serviceCharge.specialChargesKey;

      const requiresApproval =
        (props.renewalStatus === RenewalStatus.IN_PROGRESS.name ||
          props.renewalStatus === RenewalStatus.APPROVAL_REQUIRED.name) &&
        serviceCharge.isApprovalRequired;

      return {
        display: inEditMode ? (
          <FormControl>
            <Input
              fullWidth
              type="number"
              inputProps={{
                step: 0.01
              }}
              value={getServiceChargeValue(serviceCharge)}
              disabled={!isMutable(serviceCharge)}
              onChange={(event) => {
                if (
                  !checkPrecisionLength(
                    event.target.value,
                    ADDITIONAL_CHARGES_PRECISION
                  )
                )
                  props.updateServiceCharge(
                    serviceCharge.specialChargesKey,
                    !!event.target.value ? Number(event.target.value) : '',
                    false
                  );
              }}
              startAdornment={getServiceChargeStartAdornment(serviceCharge)}
              endAdornment={getServiceChargeEndAdornment(serviceCharge)}
            />
          </FormControl>
        ) : baseCharge !== adjustedCharge ? (
          <div>
            <div style={{ textDecoration: 'line-through' }}>
              {getServiceChargeBaseValue(
                serviceCharge,
                props.roundValues,
                true
              )}
            </div>
            <div style={{ color: requiresApproval ? 'red' : 'black' }}>
              {getServiceChargeValue(serviceCharge, props.roundValues, true)}
            </div>
          </div>
        ) : (
          <div>
            {getServiceChargeBaseValue(serviceCharge, props.roundValues, true)}
          </div>
        ),
        shared: {
          className: 'only-padding-left-5'
        }
      };
    }
  },
  {
    header: {
      display: ''
    },
    accessor: (serviceCharge) => {
      const deviation = serviceChargeDeviation(
        serviceCharge,
        props.roundValues
      );

      return {
        display: (
          <div
            style={{
              color: serviceCharge.isApprovalRequired ? 'red' : 'black'
            }}
          >
            {deviation !== 0
              ? `(${deviation.toFixed(PERCENTAGE_PRECISION)}%)`
              : ''}
          </div>
        )
      };
    },
    shared: {
      className: 'only-padding-left-5'
    }
  },
  {
    header: {
      display: ''
    },
    accessor: (serviceCharge) => {
      const currentValue = getServiceChargeValue(serviceCharge);
      const baseValue = getServiceChargeBaseValue(serviceCharge);

      const canEdit =
        !props.disabled &&
        !serviceCharge.disabled &&
        !IMMUTABLE_SERVICE_CHARGE_DESCS.includes(
          serviceCharge.specialChargeCode
        );

      const inEditMode =
        currentEditableCharge === serviceCharge.specialChargesKey;

      const editIcon = inEditMode ? 'save' : 'edit';
      return {
        display: canEdit ? (
          <div style={{ display: 'flex' }}>
            <Tooltip
              title={props.intl.formatMessage({
                id: !inEditMode ? 'start_editing' : 'save_changes'
              })}
            >
              <Icon
                style={{ margin: '5px' }}
                color={
                  inEditMode && !currentValue && baseValue !== currentValue
                    ? 'disabled'
                    : 'action'
                }
                onClick={
                  !currentValue
                    ? null
                    : () => setMutableServiceCharge(serviceCharge)
                }
              >
                {editIcon}
              </Icon>
            </Tooltip>
            {inEditMode && canEdit ? (
              <Tooltip
                title={props.intl.formatMessage({
                  id: 'cancel'
                })}
              >
                <Icon
                  style={{ margin: '5px' }}
                  color={'action'}
                  onClick={() => discardEditChanges(serviceCharge)}
                >
                  {'cancel'}
                </Icon>
              </Tooltip>
            ) : null}
            {!inEditMode && canEdit && currentValue !== baseValue ? (
              <Tooltip
                title={props.intl.formatMessage({
                  id: 'revert_changes'
                })}
              >
                <Icon
                  style={{ margin: '5px' }}
                  color="action"
                  onClick={() => revertServiceCharge(serviceCharge)}
                >
                  undo
                </Icon>
              </Tooltip>
            ) : null}
          </div>
        ) : null,
        shared: {
          className: 'only-padding-left-5'
        }
      };
    }
  }
];

class RenewalsPricingInfo extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      mutableServiceCharge: null
    };

    this.setMutableServiceCharge = this.setMutableServiceCharge.bind(this);
    this.isMutable = this.isMutable.bind(this);
    this.getServiceCharges = this.getServiceCharges.bind(this);
    this.revertServiceCharge = this.revertServiceCharge.bind(this);
    this.discardEditChanges = this.discardEditChanges.bind(this);
  }

  setMutableServiceCharge(serviceCharge) {
    const stopEditing =
      this.state.mutableServiceCharge === serviceCharge.specialChargesKey;

    const originalAdjustedCharge = !stopEditing
      ? getServiceChargeValue(serviceCharge)
      : this.state.originalAdjustedCharge;

    this.setState(
      {
        mutableServiceCharge: !stopEditing
          ? serviceCharge.specialChargesKey
          : null,
        originalAdjustedCharge
      },
      () => {
        const currentValue = getServiceChargeValue(
          serviceCharge,
          this.props.roundValues
        );
        const baseValue = getServiceChargeBaseValue(
          serviceCharge,
          this.props.roundValues
        );
        if (stopEditing && currentValue !== baseValue)
          this.props.updateServiceCharge(
            serviceCharge.specialChargesKey,
            getServiceChargeValue(serviceCharge)
          );
      }
    );
  }

  discardEditChanges(serviceCharge) {
    const { originalAdjustedCharge } = this.state;

    this.setState(
      {
        mutableServiceCharge: null,
        originalAdjustedCharge: null
      },
      () => {
        this.props.updateServiceCharge(
          serviceCharge.specialChargesKey,
          originalAdjustedCharge,
          false
        );
      }
    );
  }

  isMutable(serviceCharge) {
    return (
      !this.props.disabled &&
      serviceCharge.specialChargesKey === this.state.mutableServiceCharge &&
      !IMMUTABLE_SERVICE_CHARGE_DESCS.includes(serviceCharge.specialChargeCode)
    );
  }

  revertServiceCharge(serviceCharge, inEditMode) {
    this.setState(
      {
        mutableServiceCharge: null
      },
      () =>
        this.props.updateServiceCharge(
          serviceCharge.specialChargesKey,
          getServiceChargeBaseValue(serviceCharge),
          !inEditMode
        )
    );
  }

  getServiceCharges() {
    return this.props.serviceCharges.map((serviceCharge) => ({
      ...serviceCharge,
      disabled: IMMUTABLE_SERVICE_CHARGE_DESCS.includes(
        serviceCharge.specialChargeDesc
      )
    }));
  }

  render() {
    return (
      <FormSubsection
        title={this.props.intl.formatMessage({
          id: 'service_charges'
        })}
      >
        <DataTable
          columns={getColumns(
            this.props,
            this.setMutableServiceCharge,
            this.isMutable,
            this.revertServiceCharge,
            this.discardEditChanges,
            this.state.mutableServiceCharge
          )}
          bodyData={this.getServiceCharges()}
        />
      </FormSubsection>
    );
  }
}

RenewalsPricingInfo.propTypes = {
  renewalStatus: PropTypes.bool,
  serviceCharges: PropTypes.array.isRequired,
  updateServiceCharge: PropTypes.func,
  disabled: PropTypes.bool,
  roundValues: PropTypes.bool
};

export default injectIntl(RenewalsPricingInfo);
